'use strict';
namespace Core {

    class RefreshView {

        private _initialised: boolean = false;
        private showWarning: boolean = false;

        constructor(private $q: ng.IQService,
                    private $routeParams: ng.route.IRouteParamsService,
                    private $scope: ng.IScope,
                    private $window: ng.IWindowService,
                    private basketService: any,
                    private broker: Broker,
                    private tripService: TripService,
                    private windowUtil: any)
        {
        }

        init(): void
        {
            if(this._initialised) {
                return;
            }
            this._initialised = true;

            if (!this.isLocalStorageSupported()) {
                return;
            }

            const updateData = () => {
                    if (this.$routeParams['tripId']) {
                        this.getInfoFromTrip(this.$routeParams['tripId']).then((info) => {
                            this.setData(info);
                        });
                    }
            };

            this.broker.subscribe('RefreshView', 'flightAddedToBasket', updateData);
            this.broker.subscribe('RefreshView', 'roomsAddedToBasket',  updateData);

            const selectionHandler = this.selectionChanged.bind(this);

            this.$window.addEventListener('storage', selectionHandler);

            this.$scope.$on('$destroy', () => {
                this.$window.removeEventListener('storage', selectionHandler);
                this.broker.unsubscribe('RefreshView');
            });
        }

        private selectionChanged(ev: any): void
        {
            //leave if this page is not on a trip type page
            const tripId: string = this.$routeParams['tripId'];

            if (!tripId) {
                return;
            }

            //is the current page somewhere where we'd like to monitor the basket change on?
            let pageProduct = this.productsRelatedToPage(this.$window.location.toString());
            if(pageProduct === null) {
                return;
            }

            if (ev.key === 'trip') {
                const actual: SelectedProductInfo = JSON.parse(ev.newValue);

                //check if it is our trip and if our other ids don't match
                if (actual.TripId === tripId) {

                    this.getInfoFromTrip(tripId).then( my => {
                        let displayWarning = false;

                        if (pageProduct !== null && (pageProduct & Product.Flight) === Product.Flight) {
                            displayWarning = actual.FlightInfo.ProductId !== my.FlightInfo.ProductId ||
                                actual.TripType !== my.TripType;
                        }

                        if(!displayWarning && pageProduct !== null && (pageProduct & Product.Hotel) === Product.Hotel) {
                            displayWarning = actual.HotelInfo !== my.HotelInfo
                                && actual.HotelInfo.ProductId !== my.HotelInfo.ProductId;
                        }

                        if(displayWarning) {
                            this.$scope.$evalAsync(() => {
                                this.showWarning = true;
                            });
                        }
                    });

                }
            }
        }

        private refreshPage(): void {

            //this is a bit tricky now, depending on what is in the actual trip, where we are
            //in our journey and the type of journey we want to redirect the user to the most useful point.
            const tripId = this.$routeParams['tripId'];
            const currentLocation = this.$window.location.toString();

            if (currentLocation.indexOf('/holidays/options/') > -1
                || currentLocation.indexOf('/holidays/book/') > -1
                || /\/holidays\/[a-zA-Z\-0-9]{36}\/\d{4,}/.test(currentLocation)) {

                //for holidays always go back to hotel availability if we are on the hotels details page,
                //options page or the booking form.
                //This is to handle the situation where changing flights also changes the hotel results
                //due to filtering based on proximity to airports  (Canary islands)
                this.getInfoFromTrip(tripId).then((my) => {

                    if (my.TripType === Core.Trip.TripType.Holiday) {
                        this.windowUtil.location.href('holidays/hotels/' + tripId);
                        return;
                    }

                    this.$window.location.reload();
                });
            } else {
                this.$window.location.reload();
            }
        }

        private setData (productIds: SelectedProductInfo): void {
            // date is needed to force refresh on the hotels on flights workflow on the second change to flight workflow
            localStorage.setItem('trip', JSON.stringify({ ...productIds, date: Date.now() }));
        }

        private getInfoFromTrip (tripId: string): ng.IPromise<SelectedProductInfo>{

            return this.tripService.getTrip(tripId).then((trip) => {

                const basketId = trip.BasketId;

                return this.$q.all([
                    this.basketService.getHotel(basketId),
                    this.basketService.getFlight(basketId)
                ]).then( (results: any[]) => {

                    const flightProductId =  results[1] && results[1].ProductId || null;

                    let hotelInfo:any = null;

                    if (trip.TripType === Core.Trip.TripType.Hotel
                        || trip.TripType === Core.Trip.TripType.Holiday )
                    {
                        hotelInfo = new HotelInfo(results[0] && results[0].ProductId,
                            results[0] && results[0].Details.Id,
                            trip.HotelsRequest.CheckInDate,
                            trip.HotelsRequest.CheckOutDate);
                    }


                    return new SelectedProductInfo(tripId,
                        trip.TripType,
                        new FlightInfo(flightProductId),
                        hotelInfo);
                });
            });
        }

        private getInfoFromStorage(tripId: string): SelectedProductInfo | null {
            const info: SelectedProductInfo = JSON.parse(localStorage.getItem('trip') || '');

            if (info.TripId === tripId) {
                return info;
            } else {
                return null;
            }
        }

        private isLocalStorageSupported(): boolean {
            const key = 'test';

            try {
                localStorage.setItem(key, '1');
                localStorage.removeItem(key);
                return true;
            } catch (error) {
                return false;
            }
        }

        private productsRelatedToPage(url: string): Product | null {

            let map = [
                {Regex:/\/(hotels)\/book\/[a-zA-Z\-0-9]{36}/, Products: Product.Hotel},
                {Regex:/\/(holidays)\/book\/[a-zA-Z\-0-9]{36}/, Products: Product.Hotel + Product.Flight},
                {Regex:/\/holidays\/hotels\/[a-zA-Z\-0-9]{36}/, Products: Product.Hotel + Product.Flight},
                {Regex:/\/holidays\/[a-zA-Z\-0-9]{36}\/\d{4,}/, Products: Product.Flight},
                {Regex:/\/holidays\/options\/[a-zA-Z\-0-9]{36}/, Products: Product.Hotel + Product.Flight}
            ];

            for(let i=0; i< map.length; i++){
                if (map[i].Regex.test(url)) {
                    return map[i].Products;
                }
            }

            return null;
        }


    }

    ///Enum for holding product types should be keyed so that you can use bitwise operators
    ///to decide if a product is included in a set
    enum Product {
        Flight = 1 << 0,
        Hotel = 1 << 1
    }

    class FlightInfo
    {
        constructor(public ProductId: string)
        {}
    }
    class HotelInfo
    {
        constructor(public ProductId: string,
                    public Id: number,
                    public CheckInDate: string,
                    public CheckOutDate: string)
        {}
    }
    class SelectedProductInfo
    {
        constructor(public TripId: string,
                    public TripType: number,
                    public FlightInfo: FlightInfo,
                    public HotelInfo: HotelInfo,
                    public date?: Date)
        {}
    }

    angular
        .module('core')
        .directive('refreshView', function () {

            return {
                restrict: 'E',
                replace: true,
                scope:{},
                controllerAs : 'rv',
                controller : RefreshView,
                templateUrl: 'partials/home/directivetemplate?type=RefreshView',
                link: function (scope, el, attr, controller : RefreshView) {
                    controller.init();
                }
            };
        });

}
